import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit, Trash2, ShieldCheck, Save, LockKeyhole, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

import { siteConfig } from "@/config/siteConfig";
import {
  createCoverageLocation,
  deleteCoverageLocation,
  fetchCoverageLocations,
  updateCoverageLocation,
  verifyCoverageAdmin,
} from "@/services/coverageService";
import { coverageStatusListAR, coverageUtils } from "@/utils/coverageUtils";
import { COVERAGE } from "@/constants/testIds";

const twinLogo = "/assets/logo/twin-tc-logo-transparent.png";

const getEmptyForm = () => ({
  region_name_ar: "",
  region_name_en: "",
  city: "",
  latitude: "",
  longitude: "",
  service_status: "served_active",
  description_ar: "",
  description_en: "",
  whatsapp: siteConfig.contact.whatsapp,
  phone: siteConfig.contact.phone,
  internal_notes: "Sample Data / بيانات تجريبية قابلة للتعديل",
  is_public: true,
  display_order: 100,
});

export const AdminCoveragePage = () => {
  const [adminPassword, setAdminPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);

  const selectedLocation = useMemo(
    () => locations.find((item) => item.id === selectedId) || null,
    [locations, selectedId],
  );

  const loadLocations = async () => {
    try {
      const data = await fetchCoverageLocations({
        includeHidden: true,
        status: "all",
        lang: "ar",
        adminPassword,
      });
      setLocations(data.items || []);
    } catch (error) {
      toast.error("تعذر تحميل بيانات التغطية");
    }
  };

  const unlockPanel = async () => {
    if (!adminPassword.trim()) {
      toast.error("أدخل كلمة المرور أولاً");
      return;
    }

    try {
      await verifyCoverageAdmin({ adminPassword });
      setUnlocked(true);
      await loadLocations();
      toast.success("تم فتح لوحة الإدارة");
    } catch (error) {
      toast.error("كلمة المرور غير صحيحة");
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSelectLocation = (location) => {
    setSelectedId(location.id);
    setForm({
      region_name_ar: location.region_name_ar,
      region_name_en: location.region_name_en,
      city: location.city,
      latitude: String(location.latitude),
      longitude: String(location.longitude),
      service_status: location.service_status,
      description_ar: location.description_ar,
      description_en: location.description_en,
      whatsapp: location.whatsapp || "",
      phone: location.phone || "",
      internal_notes: location.internal_notes || "",
      is_public: location.is_public,
      display_order: location.display_order,
    });
  };

  const resetForm = () => {
    setSelectedId(null);
    setForm(getEmptyForm());
  };

  const buildPayload = () => ({
    region_name_ar: form.region_name_ar.trim(),
    region_name_en: form.region_name_en.trim(),
    city: form.city.trim(),
    latitude: Number(form.latitude),
    longitude: Number(form.longitude),
    service_status: form.service_status,
    description_ar: form.description_ar.trim(),
    description_en: form.description_en.trim(),
    whatsapp: form.whatsapp.trim(),
    phone: form.phone.trim(),
    internal_notes: form.internal_notes.trim(),
    is_public: Boolean(form.is_public),
    display_order: Number(form.display_order),
  });

  const validatePayload = (payload) => {
    if (!payload.region_name_ar || !payload.region_name_en || !payload.city) return false;
    if (!payload.description_ar || !payload.description_en) return false;
    if (!Number.isFinite(payload.latitude) || !Number.isFinite(payload.longitude)) return false;
    if (payload.latitude < -90 || payload.latitude > 90) return false;
    if (payload.longitude < -180 || payload.longitude > 180) return false;
    return true;
  };

  const saveLocation = async () => {
    const payload = buildPayload();
    if (!validatePayload(payload)) {
      toast.error("تحقق من الحقول المطلوبة والإحداثيات");
      return;
    }

    setSaving(true);
    try {
      if (selectedLocation) {
        await updateCoverageLocation({
          locationId: selectedLocation.id,
          data: payload,
          adminPassword,
        });
        toast.success("تم تحديث الموقع بنجاح");
      } else {
        await createCoverageLocation({ data: payload, adminPassword });
        toast.success("تمت إضافة موقع جديد");
      }
      await loadLocations();
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "فشل حفظ الموقع");
    } finally {
      setSaving(false);
    }
  };

  const removeLocation = async (locationId) => {
    if (!window.confirm("هل تريد حذف هذا الموقع؟")) return;

    try {
      await deleteCoverageLocation({ locationId, adminPassword });
      toast.success("تم حذف الموقع");
      if (selectedId === locationId) {
        resetForm();
      }
      await loadLocations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "فشل حذف الموقع");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-ar" dir="rtl">
      <header className="coverage-topbar sticky top-0 z-40">
        <div className="container-shell flex min-h-[70px] items-center justify-between py-3">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={twinLogo} alt="TWIN TC logo" className="brand-logo logo-glow h-12 w-auto sm:h-14" />
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-xl border-white/20 bg-card/70">
              <Link to="/coverage-map">عرض الخريطة العامة</Link>
            </Button>
            <Button asChild className="rounded-xl bg-primary text-primary-foreground">
              <Link to="/">العودة للرئيسية</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container-shell py-8">
        <Card className="glass-card mb-6 border-white/12">
          <CardHeader>
            <CardTitle className="text-2xl">لوحة إدارة خريطة التغطية</CardTitle>
            <CardDescription>
              حماية مؤقتة بكلمة مرور من إعدادات الخادم. تأكد من تغيير كلمة المرور قبل الإنتاج.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!unlocked ? (
              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(event) => setAdminPassword(event.target.value)}
                  placeholder="أدخل كلمة مرور الإدارة"
                  data-testid={COVERAGE.adminPasswordInput}
                />
                <Button className="rounded-xl bg-primary text-primary-foreground" onClick={unlockPanel} data-testid={COVERAGE.adminUnlockButton}>
                  <LockKeyhole className="me-2 h-4 w-4" /> فتح الإدارة
                </Button>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-xl border border-[#2ef2a5]/30 bg-[#2ef2a5]/15 px-4 py-2 text-sm">
                <ShieldCheck className="h-4 w-4 text-[#2ef2a5]" /> تم فتح لوحة الإدارة
              </div>
            )}
          </CardContent>
        </Card>

        {unlocked ? (
          <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
            <Card className="glass-card border-white/12">
              <CardHeader>
                <CardTitle>{selectedLocation ? "تعديل الموقع" : "إضافة موقع جديد"}</CardTitle>
                <CardDescription>جميع الحقول الأساسية مطلوبة. البيانات الحالية Sample Data قابلة للتعديل.</CardDescription>
              </CardHeader>
              <CardContent>
                <form className="grid gap-4" data-testid={COVERAGE.adminForm} onSubmit={(e) => e.preventDefault()}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>اسم المنطقة (AR)</Label>
                      <Input value={form.region_name_ar} onChange={(e) => setField("region_name_ar", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Region Name (EN)</Label>
                      <Input value={form.region_name_en} onChange={(e) => setField("region_name_en", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>المدينة / المحافظة</Label>
                      <Input value={form.city} onChange={(e) => setField("city", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>حالة الخدمة</Label>
                      <Select value={form.service_status} onValueChange={(value) => setField("service_status", value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {coverageStatusListAR.map((status) => (
                            <SelectItem value={status.value} key={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Latitude</Label>
                      <Input type="number" value={form.latitude} onChange={(e) => setField("latitude", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Longitude</Label>
                      <Input type="number" value={form.longitude} onChange={(e) => setField("longitude", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp</Label>
                      <Input value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>ترتيب الظهور</Label>
                      <Input type="number" value={form.display_order} onChange={(e) => setField("display_order", e.target.value)} />
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2">
                      <div className="inline-flex items-center gap-2 text-sm">
                        {form.is_public ? <Eye className="h-4 w-4 text-[#2ef2a5]" /> : <EyeOff className="h-4 w-4 text-[#ff5c7a]" />}
                        <span>ظاهر للعامة</span>
                      </div>
                      <Switch checked={form.is_public} onCheckedChange={(checked) => setField("is_public", checked)} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>الوصف المختصر (AR)</Label>
                    <Textarea rows={3} value={form.description_ar} onChange={(e) => setField("description_ar", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Description (EN)</Label>
                    <Textarea rows={3} value={form.description_en} onChange={(e) => setField("description_en", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>ملاحظات داخلية</Label>
                    <Textarea rows={2} value={form.internal_notes} onChange={(e) => setField("internal_notes", e.target.value)} />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button className="rounded-xl bg-primary text-primary-foreground" onClick={saveLocation} disabled={saving} data-testid={COVERAGE.adminSaveButton}>
                      <Save className="me-2 h-4 w-4" /> {saving ? "جاري الحفظ..." : selectedLocation ? "حفظ التعديلات" : "إضافة الموقع"}
                    </Button>
                    <Button variant="outline" className="rounded-xl border-white/20 bg-card/70" onClick={resetForm} data-testid={COVERAGE.adminAddButton}>
                      <Plus className="me-2 h-4 w-4" /> جديد
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/12">
              <CardHeader>
                <CardTitle>المواقع الحالية</CardTitle>
                <CardDescription>يمكنك تعديل أو حذف أي موقع من القائمة.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>المنطقة</TableHead>
                      <TableHead>الحالة</TableHead>
                      <TableHead>المدينة</TableHead>
                      <TableHead>إجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map((location) => {
                      const statusMeta = coverageUtils.getCoverageStatusMeta(location.service_status, "ar");

                      return (
                        <TableRow key={location.id}>
                          <TableCell>
                            <p className="text-sm font-semibold">{location.region_name_ar}</p>
                            <p className="text-xs text-muted-foreground">{location.region_name_en}</p>
                          </TableCell>
                          <TableCell>
                            <Badge style={{ backgroundColor: `${statusMeta.color}25`, color: statusMeta.color, borderColor: `${statusMeta.color}66` }}>
                              {statusMeta.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{location.city}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-lg border-white/20 bg-card/70"
                                onClick={() => onSelectLocation(location)}
                                data-testid={COVERAGE.adminEditButton}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-lg border-[#ff5c7a]/40 bg-[#ff5c7a]/20"
                                onClick={() => removeLocation(location.id)}
                                data-testid={COVERAGE.adminDeleteButton}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </main>
    </div>
  );
};
